import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { headers } from '../../styles/type';

import IconCard from '../IconCard';
import TextRenderer from '../TextRenderer';
import Background from '../Background';

const StyledBackground = styled(Background)`

`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: ${rem(40)} 0 ${rem(20)} 0;
  ${containerStyles.regular}
`;

const Headline = styled.h2`
  ${headers.h3}
  text-align: center;
  color: ${colors.blue['800']};
  max-width: ${rem(800)};
  margin: 0 auto ${rem(40)} auto;

  ${({ theme }) => (theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `)}
`;

const Grid = styled.div`
  display: grid;
  gap: ${rem(40)};

  ${({ columns }) => !columns && `
    grid-template-columns: repeat(auto-fit, minmax(${rem(300)}, 1fr));
  `}

  ${({ columns, totalItems }) => !columns && totalItems < 3 && `
    justify-content: center;
    margin: 0 auto;
    max-width: ${totalItems === 2 ? rem(1000) : rem(480)};
  `}

  ${({ columns }) => columns && `
    grid-template-rows: auto;
    grid-template-columns: 1fr;

    ${media('>tiny')} {
      grid-template-columns: repeat(2, 1fr);
    }

    ${media('>medium')} {
      grid-template-columns: repeat(${columns}, 1fr)
    }
  `}
`;

const IconCards = ({
  headline, iconCards, roundedImages, backgroundColor = 'White', columns, backgroundPattern
}) => (
  <StyledBackground
    color={backgroundColor}
    pattern={backgroundPattern}
    patternVariation={null}
  >
    <ContentWrapper>
      {headline && (
        <Headline>
          <TextRenderer text={headline} />
        </Headline>
      )}
      <Grid columns={columns}>
        {iconCards?.map((iconCard, index) => (
          <IconCard
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            image={iconCard.image}
            headline={iconCard.headline}
            subhead={iconCard.subhead}
            text={iconCard.text}
            ctaText={iconCard.ctaText}
            ctaLink={iconCard.ctaLink}
            secondaryCtaText={iconCard.secondaryCtaText}
            secondaryCtaLink={iconCard.secondaryCtaLink}
            rounded={roundedImages}
          />
        ))}
      </Grid>
    </ContentWrapper>
  </StyledBackground>
);

IconCards.propTypes = {
  headline: PropTypes.string,
  iconCards: PropTypes.arrayOf(
    PropTypes.shape(IconCard.propTypes)
  ),
  roundedImages: PropTypes.bool,
  backgroundColor: PropTypes.oneOf(['White', 'Light Blue']),
  backgroundPattern: PropTypes.oneOf(['Splash Dots', 'Large Dots']),
  columns: PropTypes.string
};

export const query = graphql`
  fragment IconCardsFragment on ContentfulIconCards {
    id
    __typename
    headline
    iconCards {
      ...IconCardFragment
    }
    roundedImages
    backgroundColor
    backgroundPattern
    columns
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default IconCards;
